import { Amplify } from "@aws-amplify/core";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { Sha256 } from "@aws-crypto/sha256-js";
import amplifyConfig from "@/config/amplifyConfig";

type AmplifyAuthModule = typeof import("aws-amplify/auth");
type SignInWithRedirectInput = NonNullable<
  Parameters<AmplifyAuthModule["signInWithRedirect"]>[0]
>;
type ProviderOptions = NonNullable<SignInWithRedirectInput["provider"]>;

const GOOGLE_WORKSPACE_PROVIDER: ProviderOptions = {
  custom: "GoogleWorkspace"
};

function getProviderIdentity(provider: ProviderOptions) {
  return typeof provider === "string" ? provider : provider.custom;
}

let configured = false;

const OAUTH_KEY_PREFIX = "CognitoIdentityServiceProvider";
const CODE_VERIFIER_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const FALLBACK_PKCE_KEY = "tempstars.oauth.pkce";
const FALLBACK_STATE_KEY = "tempstars.oauth.state";

export function configureIfNeeded() {
  if (!configured) {
    Amplify.configure(amplifyConfig);
    configured = true;
  }
}

export async function loginWithGoogleWorkspace() {
  configureIfNeeded();
  const authConfig = (Amplify.getConfig().Auth as any)?.Cognito;

  if (!authConfig) {
    throw new Error("Amplify Auth is not configured with Cognito settings.");
  }

  const oauthConfig = authConfig.loginWith?.oauth;
  if (!oauthConfig) {
    throw new Error("OAuth configuration missing from Amplify Auth settings.");
  }

  const redirectUri = resolveRedirectUri(oauthConfig.redirectSignIn);
  const state = generateRandomState();
  const { value: codeVerifier, codeChallenge } = generateCodeVerifier(128);

  persistOAuthArtifacts({
    clientId: authConfig.userPoolClientId,
    state,
    codeVerifier,
    preferPrivateSession: false
  });

  const params = new URLSearchParams([
    ["redirect_uri", redirectUri],
    ["response_type", oauthConfig.responseType ?? "code"],
    ["client_id", authConfig.userPoolClientId],
    ["identity_provider", getProviderIdentity(GOOGLE_WORKSPACE_PROVIDER)],
    ["scope", (oauthConfig.scopes ?? ["email", "openid", "profile"]).join(" ")],
    ["state", state]
  ]);

  if ((oauthConfig.responseType ?? "code") === "code") {
    params.append("code_challenge", codeChallenge);
    params.append("code_challenge_method", "S256");
  }

  const authorizeUrl = `https://${
    oauthConfig.domain
  }/oauth2/authorize?${params.toString()}`;
  window.location.assign(authorizeUrl);
}

export async function sessionCheck(
  options?: Parameters<typeof fetchAuthSession>[0]
) {
  configureIfNeeded();
  const finalOptions = options ?? { forceRefresh: true };
  return fetchAuthSession(finalOptions);
}

export async function logout() {
  configureIfNeeded();
  return signOut();
}

function resolveRedirectUri(redirectSignIn: string | string[]) {
  if (Array.isArray(redirectSignIn)) {
    const currentOrigin = window.location.origin;
    const match = redirectSignIn.find(uri => uri.startsWith(currentOrigin));
    return match ?? redirectSignIn[0];
  }

  return redirectSignIn;
}

function generateRandomState(length = 32) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return bufferToBase64Url(array);
}

function bufferToBase64Url(buffer: Uint8Array) {
  let binary = "";
  buffer.forEach(byte => {
    binary += String.fromCharCode(byte);
  });

  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

type OAuthArtifacts = {
  clientId: string;
  state: string;
  codeVerifier: string;
  preferPrivateSession: boolean;
};

function persistOAuthArtifacts(
  artifacts: OAuthArtifacts,
  storage: Storage = window.localStorage
) {
  const keys = getOAuthStorageKeys(artifacts.clientId);
  const storages: Array<{ label: string; instance: Storage }> = [
    { label: "localStorage", instance: window.localStorage },
    { label: "sessionStorage", instance: window.sessionStorage }
  ];

  storages.forEach(({ label, instance }) => {
    try {
      instance.setItem(keys.inflight, "true");
      instance.setItem(keys.pkce, artifacts.codeVerifier);
      instance.setItem(keys.state, artifacts.state);
      instance.setItem(
        keys.signIn,
        `${true},${artifacts.preferPrivateSession ? "true" : "false"}`
      );
      instance.setItem(FALLBACK_PKCE_KEY, artifacts.codeVerifier);
      instance.setItem(FALLBACK_STATE_KEY, artifacts.state);
    } catch (error) {
      console.warn(
        `[AUTH] Failed to persist OAuth artifacts to ${label}`,
        error
      );
    }
  });
}

function getOAuthStorageKeys(clientId: string) {
  return {
    inflight: `${OAUTH_KEY_PREFIX}.${clientId}.inflightOAuth`,
    pkce: `${OAUTH_KEY_PREFIX}.${clientId}.oauthPKCE`,
    state: `${OAUTH_KEY_PREFIX}.${clientId}.oauthState`,
    signIn: `${OAUTH_KEY_PREFIX}.${clientId}.oauthSignIn`
  };
}

function generateCodeVerifier(length: number) {
  const randomBytes = new Uint8Array(length);
  window.crypto.getRandomValues(randomBytes);

  let value = "";
  for (const byte of randomBytes) {
    value += CODE_VERIFIER_CHARSET.charAt(byte % CODE_VERIFIER_CHARSET.length);
  }

  const codeChallenge = computeCodeChallenge(value);
  return { value, codeChallenge };
}

function computeCodeChallenge(codeVerifier: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const sha = new Sha256();
  sha.update(data);
  const digest = new Uint8Array(sha.digestSync());
  return bufferToBase64Url(digest);
}

export function readOAuthArtifact(key: string) {
  try {
    const localValue = window.localStorage.getItem(key);
    if (localValue) {
      return localValue;
    }
  } catch (error) {
    console.warn(
      "[AUTH] Failed to read OAuth artifact from localStorage",
      error
    );
  }

  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    console.warn(
      "[AUTH] Failed to read OAuth artifact from sessionStorage",
      error
    );
    return null;
  }
}

export function clearOAuthArtifacts(clientId: string) {
  const keys = getOAuthStorageKeys(clientId);
  const storages = [window.localStorage, window.sessionStorage];

  storages.forEach(storage => {
    try {
      storage.removeItem(keys.inflight);
      storage.removeItem(keys.pkce);
      storage.removeItem(keys.state);
      storage.removeItem(keys.signIn);
      storage.removeItem(FALLBACK_PKCE_KEY);
      storage.removeItem(FALLBACK_STATE_KEY);
    } catch (error) {
      console.warn("[AUTH] Failed to clear OAuth artifacts", error);
    }
  });
}
